import React from "react";
import { Box, IconButton, Typography } from "@mui/material";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import SpeedIcon from "@mui/icons-material/Speed";
import GroupsIcon from "@mui/icons-material/Groups";
import { useTheme } from "../../ThemeContext";

const BenchSummary = ({
  benchSummary,
  selectedDate,
  selectedDepartment = "All Departments",
  noData,
  onClose,
}) => {
  const { isDarkMode } = useTheme();

  const cardBg = isDarkMode ? "rgba(255, 255, 255, 0.04)" : "#FFFFFF";
  const borderColor = isDarkMode ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.06)";
  const shadowColor = isDarkMode ? "rgba(0, 0, 0, 0.4)" : "rgba(0, 0, 0, 0.05)";

  const totalBenchStaffs = benchSummary?.total_bench_users ?? 0;

  const metricCards = [
    {
      count: benchSummary?.["8_hours_bench_users"] ?? 0,
      label: "In Bench Staff",
      subtext: (
        <>
          In <span style={{ color: "#FF3B30", fontWeight: 700 }}>8 Hours</span>
        </>
      ),
      color: "#FF3B30", // Red (Interchanged)
      icon: <SpeedIcon className="bench-icon-red" sx={{ fontSize: 20, color: "#FF3B30 !important" }} />,
    },
    {
      count: benchSummary?.["4_hours_bench_users"] ?? 0,
      label: "In Bench Staff",
      subtext: (
        <>
          In <span style={{ color: "#FF9F0A", fontWeight: 700 }}>&ge; 4 Hours</span>
        </>
      ),
      color: "#FF9F0A", // Orange
      icon: <AccessTimeIcon className="bench-icon-orange" sx={{ fontSize: 20, color: "#FF9F0A !important" }} />,
    },
    {
      count: benchSummary?.["less_than_4_hours"] ?? 0,
      label: "In Bench Staff",
      subtext: (
        <>
          In <span style={{ color: "#008ddf", fontWeight: 700 }}>&lt; 4 Hours</span>
        </>
      ),
      color: "#008ddf", // Blue (Interchanged)
      icon: <HourglassEmptyIcon className="bench-icon-blue" sx={{ fontSize: 20, color: "#008ddf !important" }} />,
    },
    {
      count: benchSummary?.["shortly_available"] ?? 0,
      label: "Shortly",
      subtext: (
        <>
          <span style={{ color: "#12b79f", fontWeight: 700 }}>Available</span> Staff
        </>
      ),
      color: "#12b79f", // App Green
      icon: <FlashOnIcon className="bench-icon-green" sx={{ fontSize: 20, color: "#12b79f !important" }} />,
    },
  ];

  const hasNoData =
    Boolean(noData) ||
    (totalBenchStaffs === 0 && !metricCards.some((c) => c.count > 0));

  return (
    <Box sx={{ paddingX: 1, paddingY: 1.5 }}>
      {/* Header */}
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px",
          borderBottom: `1px solid ${borderColor}`,
          paddingBottom: "16px",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            fontSize: "1.15rem",
            color: isDarkMode ? "#FFFFFF" : "#1E293B",
            letterSpacing: "-0.025em",
          }}
        >
          Bench Summary
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            color: isDarkMode ? "rgba(255, 255, 255, 0.5)" : "rgba(30, 41, 59, 0.5)",
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              backgroundColor: isDarkMode ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.05)",
              color: isDarkMode ? "#FFFFFF" : "#0F172A",
              transform: "rotate(90deg)",
            },
          }}
        >
          <HighlightOffIcon />
        </IconButton>
      </Box>

      {/* Main Count Banner (Border Radius set to 6px) */}
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: "14px",
          paddingX: "18px",
          paddingY: "14px",
          // backgroundColor: isDarkMode ? "rgba(226, 27, 90, 0.1)" : "#FFF0F3",
          backgroundColor: isDarkMode ? "rgba(18, 183, 159, 0.1)" : "rgba(18, 183, 159, 0.08)",
          // border: `1px solid ${isDarkMode ? "rgba(226, 27, 90, 0.2)" : "rgba(226, 27, 90, 0.15)"}`,
          border: `1px solid ${isDarkMode ? "rgba(18, 183, 159, 0.2)" : "rgba(18, 183, 159, 0.15)"}`,
          borderRadius: "6px",
          boxShadow: `0 4px 12px ${shadowColor}`,
          marginBottom: "24px",
        }}
      >
        <Box
          sx={{
            // backgroundColor: isDarkMode ? "rgba(226, 27, 90, 0.2)" : "rgba(226, 27, 90, 0.08)",
            backgroundColor: isDarkMode ? "rgba(18, 183, 159, 0.2)" : "rgba(18, 183, 159, 0.08)",
            width: 42,
            height: 42,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "50%",
            // border: `1px solid ${isDarkMode ? "rgba(226, 27, 90, 0.3)" : "rgba(226, 27, 90, 0.1)"}`,
            border: `1px solid ${isDarkMode ? "rgba(18, 183, 159, 0.3)" : "rgba(18, 183, 159, 0.1)"}`,
            flexShrink: 0,
          }}
        >
          <GroupsIcon sx={{ fontSize: 22, color: "#12b79f" }} />
        </Box>
        <Typography
          sx={{
            fontSize: "0.85rem",
            // color: isDarkMode ? "#FDA4AF" : "#E21B5A",
            color: isDarkMode ? "rgba(18, 183, 159, 1)" : "#12b79f",
            fontWeight: 700,
            flexGrow: 1,
            lineHeight: 1.2,
          }}
        >
          Total Bench Staff Count
        </Typography>
        <Typography
          sx={{
            fontSize: "1.9rem",
            fontWeight: 800,
            // color: "#E21B5A",
            color: "#12b79f",
            lineHeight: 1,
            flexShrink: 0,
          }}
        >
          {totalBenchStaffs}
        </Typography>
      </Box>

      {/* Grid Layout of Metric Cards or No Data Found */}
      {hasNoData ? (
        <Box
          sx={{
            width: "100%",
            height: "180px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "6px",
            backgroundColor: isDarkMode ? "#272626" : "#f0f0f0",
            color: isDarkMode ? "white" : "#888",
            fontSize: "16px",
            fontWeight: "bold",
          }}
        >
          No data found
        </Box>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: "16px",
            width: "100%",
          }}
        >
          {metricCards.map((card, index) => (
            <Box
              key={index}
              sx={{
                backgroundColor: cardBg,
                border: `1px solid ${borderColor}`,
                borderRadius: "6px",
                padding: "16px",
                display: "flex",
                flexDirection: "column",
                position: "relative",
                overflow: "hidden",
                boxShadow: `0 2px 4px ${shadowColor}`,
              }}
            >
              {/* Card Header (Icon & Count) */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  marginBottom: "12px",
                }}
              >
                <Box
                  sx={{
                    backgroundColor: isDarkMode ? "rgba(255, 255, 255, 0.03)" : "rgba(0, 0, 0, 0.02)",
                    borderRadius: "6px",
                    width: "36px",
                    height: "36px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: `1px solid ${borderColor}`,
                  }}
                >
                  {card.icon}
                </Box>
                <Typography
                  sx={{
                    fontSize: "1.85rem",
                    fontWeight: 800,
                    color: isDarkMode ? "#F8FAFC" : "#0F172A",
                    lineHeight: 1,
                    letterSpacing: "-0.03em",
                  }}
                >
                  {card.count}
                </Typography>
              </Box>

              {/* Card Content */}
              <Typography
                sx={{
                  fontWeight: 500,
                  fontSize: "0.78rem",
                  color: isDarkMode ? "#94A3B8" : "#64748B",
                  marginBottom: "2px",
                  lineHeight: 1.2,
                }}
              >
                {card.label}
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.78rem",
                  color: isDarkMode ? "#94A3B8" : "#64748B",
                  fontWeight: 500,
                  lineHeight: 1.2,
                }}
              >
                {card.subtext}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* Date & Department matching 2-column card grid */}
      {(selectedDate || selectedDepartment) && (
        <Box
          sx={{
            marginTop: "16px",
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: "16px",
            width: "100%",
          }}
        >
          {selectedDate && (
            <Box
              sx={{
                backgroundColor: cardBg,
                border: `1px solid ${borderColor}`,
                borderRadius: "6px",
                padding: "10px 12px",
                display: "flex",
                alignItems: "center",
                boxShadow: `0 2px 4px ${shadowColor}`,
                fontSize: "0.80rem",
                fontWeight: 600,
                color: isDarkMode ? "#94A3B8" : "#64748B",
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
              }}
            >
              <span style={{ flexShrink: 0 }}>Date : </span>&nbsp;
              <span
                style={{
                  color: isDarkMode ? "#FFFFFF" : "#1E293B",
                  fontWeight: 700,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {selectedDate}
              </span>
            </Box>
          )}

          {selectedDepartment && (
            <Box
              sx={{
                backgroundColor: cardBg,
                border: `1px solid ${borderColor}`,
                borderRadius: "6px",
                padding: "10px 12px",
                display: "flex",
                alignItems: "center",
                boxShadow: `0 2px 4px ${shadowColor}`,
                fontSize: "0.80rem",
                fontWeight: 600,
                color: isDarkMode ? "#94A3B8" : "#64748B",
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                cursor: "default",
              }}
            >
              <span style={{ flexShrink: 0 }}>Department : </span>&nbsp;
              <span
                style={{
                  color: isDarkMode ? "#FFFFFF" : "#1E293B",
                  fontWeight: 700,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {selectedDepartment}
              </span>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default BenchSummary;